import { ICartProps, ILineItem } from '../CartContext.types';
import { DefaultLineItemPromoResponse, IPromoSummary, IPromoHook } from './Promo.types';
import {
    DefaultPromo,
    DiscountType,
    ILineDiscount,
    IPromo,
    ITradeInThreshold
} from '../../../types/Promo';

export const usePromo = (): IPromoHook => {
    const calcBestPromos = (cart: ICartProps) => {
        cart.lineItems.forEach(calcBestPromoForLine);

        validateBogoDiscounts(cart);
    };

    const calcBestPromoForLine = (lineItem: ILineItem) => {
        if (!lineItem.promoResponses || lineItem.promoResponses.length === 0) return;

        const promoSummary: IPromoSummary = {
            dueMonthly: 0,
            dueToday: 0,
            hasBundleDiscount: false,
            bundleDiscount: 0,
            bundleDiscountPromo: DefaultPromo(),
            hasPromo: false,
            discount: 0,
            promoResponse: DefaultLineItemPromoResponse()
        };

        lineItem.promoResponses.forEach((p) => {
            if (p.promo.conditions.isBundle) {
                promoSummary.bundleDiscount = calculateBundleDiscount(p.promo, lineItem);
                promoSummary.bundleDiscountPromo = p.promo;
                promoSummary.hasBundleDiscount = true;
            } else {
                const discount = calculateDiscount(p.promo, lineItem);
                if (discount > promoSummary.discount) {
                    promoSummary.discount = discount;
                    promoSummary.promoResponse = p;
                    promoSummary.hasPromo = true;
                }
            }
        });

        if (lineItem.device?.useRetailPrice) {
            promoSummary.dueToday = promoSummary.discount;
        } else {
            promoSummary.dueMonthly = Number(
                (promoSummary.discount / (lineItem.device?.product.pricing.numMonths ?? 1)).toFixed(
                    2
                )
            );
        }

        lineItem.promoSummary = promoSummary;
    };

    const calculateTradeInPromoDeduction = (promo: IPromo, lineItem: ILineItem): number => {
        let tradeInDeduction = 0;

        if (promo?.conditions?.tradeIn && promo?.conditions?.deductTradeInFromPromo) {
            const tradeInValue = lineItem.device?.tradeIn?.price ?? 0;

            // By default, reduce the promo amount by the trade-in amount when deductTradeInFromPromo is set.
            tradeInDeduction = tradeInValue;

            if (promo.conditions.tradeInThresholds?.length) {
                // Scale back promo amount based on trade-in thresholds.
                const discountAmount = promo.dealInfo?.discount?.value ?? 0;
                const tradeInThreshold = promo.conditions.tradeInThresholds
                    .sort((a, b) => a.minTradeInValue - b.minTradeInValue)
                    .findLast(
                        (t: ITradeInThreshold) =>
                            t.minTradeInValue <= tradeInValue &&
                            (!t.maxTradeInValue || tradeInValue <= t.maxTradeInValue)
                    );

                // When evaluating trade-in thresholds, assume customer doesn't qualify for discount
                // buy returning the full discount amount to cancel it out.
                tradeInDeduction = discountAmount;

                if (tradeInThreshold) {
                    // The trade-in amount falls within a threshold range;
                    // determine the amount by which the promo should be reduced
                    // so it doesn't exceed the max promo amount specified in the
                    // threshold configuration.
                    const upperTradeInLimit =
                        tradeInThreshold.maxTradeInValue ?? tradeInThreshold.minTradeInValue;
                    const maxCombinedAmount =
                        tradeInThreshold.maxDiscountAmount + upperTradeInLimit;

                    // Calculate the total phone price.
                    const phonePrice =
                        (lineItem.device?.monthlyPrice ?? 0) *
                        (lineItem.device?.product.pricing.numMonths ?? 1);

                    if (phonePrice >= maxCombinedAmount) {
                        // Allow promo amount to be up to, but not over max discount amount.
                        tradeInDeduction = discountAmount - (maxCombinedAmount - tradeInValue);
                    } else if (phonePrice >= tradeInValue) {
                        // Allow promo amount to be the difference in value between the phone price and the value of the trade-in.
                        tradeInDeduction = discountAmount - (phonePrice - tradeInValue);
                    }
                }
            }
        }

        // Do not allow a negative amount to be returned.
        return tradeInDeduction > 0 ? tradeInDeduction : 0;
    };

    const calculateDiscount = (promo: IPromo, lineItem: ILineItem): number => {
        if (!lineItem.device) return 0;

        const tradeInDeduction = calculateTradeInPromoDeduction(promo, lineItem);
        const lineDiscountDeduction = calculateLineDiscount(promo, lineItem);

        if (promo.dealInfo.discount.type === DiscountType.Percent) {
            return Math.abs(
                (lineItem.device.productSku?.sku.price ?? 0) *
                    (promo.dealInfo.discount.value / 100) -
                    tradeInDeduction -
                    lineDiscountDeduction
            );
        } else {
            return Math.abs(
                promo.dealInfo.discount.value - tradeInDeduction - lineDiscountDeduction
            );
        }
    };

    const calculateLineDiscount = (promo: IPromo, lineItem: ILineItem): number => {
        if (!promo?.dealInfo?.lineDiscounts?.length) {
            return 0;
        }

        const lineDiscounts = promo.dealInfo.lineDiscounts.filter((discount: ILineDiscount) => {
            return discount.lineNumber === lineItem.lineNumber;
        });

        const numberOfMonths = lineItem.device?.product.pricing.numMonths ?? 1;
        return (lineDiscounts.length ? lineDiscounts[0].value : 0) * numberOfMonths;
    };

    const calculateBundleDiscount = (promo: IPromo, lineItem: ILineItem): number => {
        const lineDiscountDeduction = calculateLineDiscount(promo, lineItem);

        switch (promo.dealInfo.discount.type) {
            case DiscountType.FlatAmount:
                return promo.dealInfo.discount.value - lineDiscountDeduction;
            case DiscountType.Percent:
                return (
                    (lineItem.planSelection?.linePrice ?? 0) *
                        (promo.dealInfo.discount.value / 100) -
                    lineDiscountDeduction
                );
        }
    };

    const validateBogoDiscounts = (cart: ICartProps) => {
        const benefitProducts = cart.lineItems.filter(
            (li) => li.promoSummary?.promoResponse.isBogo
        );

        if (benefitProducts.length <= 1) return;

        const bogoPromos = benefitProducts.flatMap((b) => b.promoSummary?.promoResponse.promo.id);

        const uniquePromoIds = new Set([...bogoPromos]);

        for (const promoId of uniquePromoIds) {
            const promoBeneficiaries = benefitProducts.filter(
                (l) => l.promoSummary?.promoResponse.promo.id === promoId
            );

            const maxBogos = Math.floor(promoBeneficiaries.length / 2);

            const sortedByDiscount = promoBeneficiaries.sort((a, b) => {
                const aVal = a.promoSummary?.discount ?? 0;
                const bVal = b.promoSummary?.discount ?? 0;
                if (aVal > bVal) return -1;
                else if (aVal < bVal) return 1;
                else return 0;
            });

            const lesserBeneficiaries = sortedByDiscount.splice(
                maxBogos,
                sortedByDiscount.length - 1
            );

            const disqualified = lesserBeneficiaries.splice(0, maxBogos);
            disqualified.forEach((d) => delete d.promoSummary);

            lesserBeneficiaries.forEach((b) => {
                delete b.promoSummary;
                b.promoResponses = b.promoResponses?.filter((r) => !r.isBogo);
                calcBestPromoForLine(b);
            });
        }
    };

    return {
        calcBestPromoForLine,
        calcBestPromos
    };
};
